import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import qrcode from 'qrcode-generator';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Landmark, Download, Loader2, ExternalLink } from 'lucide-react';

const Row = ({ label, value, testId, mono }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500 shrink-0">{label}</span>
    <span className={`text-right break-all font-medium text-slate-800 ${mono ? 'font-mono text-xs' : ''}`} data-testid={testId}>{value ?? '-'}</span>
  </div>
);

const QrImage = ({ url }) => {
  const qr = qrcode(0, 'M');
  qr.addData(url);
  qr.make();
  return <div className="w-32 h-32" dangerouslySetInnerHTML={{ __html: qr.createSvgTag({ cellSize: 3, margin: 0, scalable: true }) }} />;
};

export const EcfDataButton = ({ invoice }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const abrir = async () => {
    setOpen(true);
    setData(null);
    try {
      const resp = await axios.get(`${API}/invoices/${invoice.id}/ecf`);
      setData(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error cargando datos DGII');
      setOpen(false);
    }
  };

  const descargarXml = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/invoices/${invoice.id}/ecf/xml`, { responseType: 'blob' });
      const name = (resp.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `${invoice.ncf}.xml`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Error descargando XML');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={abrir} title="Ver Datos DGII" className="text-indigo-600 hover:bg-indigo-50" data-testid={`ecf-data-btn-${invoice.numero_factura}`}>
        <Landmark size={16} />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="ecf-data-dialog">
          <DialogHeader>
            <DialogTitle>Datos DGII</DialogTitle>
            <DialogDescription>Factura {invoice.numero_factura} · e-NCF {invoice.ncf}</DialogDescription>
          </DialogHeader>
          {!data ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-indigo-500" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <Row label="Estado" value={data.ecf_estado || 'Pendiente de envío'} testId="ecf-data-estado" />
                <Row label="Código" value={data.ecf_codigo} testId="ecf-data-codigo" />
                <Row label="TrackId" value={data.ecf_track_id || '-'} testId="ecf-data-trackid" mono />
                <Row label="Código de Seguridad" value={data.ecf_codigo_seguridad || '-'} testId="ecf-data-codigo-seguridad" mono />
                <Row label="Fecha de Firma" value={data.ecf_fecha_firma || '-'} testId="ecf-data-fecha-firma" />
                <Row label="Intentos de envío" value={data.ecf_intentos ?? 0} testId="ecf-data-intentos" />
                <Row label="Último intento" value={data.ecf_ultimo_intento ? new Date(data.ecf_ultimo_intento).toLocaleString('es-DO') : '-'} testId="ecf-data-ultimo-intento" />
                {data.ecf_error && <Row label="Error" value={<span className="text-red-600">{data.ecf_error}</span>} testId="ecf-data-error" />}
              </div>
              {data.ecf_qr && (
                <div className="flex items-center gap-4 rounded-lg border p-3">
                  <QrImage url={data.ecf_qr} />
                  <a href={data.ecf_qr} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:underline flex items-center gap-1" data-testid="ecf-data-qr-link">
                    Consultar en DGII <ExternalLink size={14} />
                  </a>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={descargarXml} disabled={downloading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ecf-download-xml-btn">
                  {downloading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Download className="mr-2" size={16} />}
                  Descargar XML
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
